import { ApplicationFormValues } from '../../../../LoanDetailTypes';
import { applicationAPI } from '../../../../../../../services/apis';
import {
  ClauseOp,
  FeatureType,
  LogicOp,
  RuleType,
} from '../../../../../../../constants';
import { Subscriber } from '../../interfaces/Subscriber';

export interface Rule {
  id: number;
  name: string;
  rosefield_app_field_name: string;
  operator: ClauseOp;
  value: unknown;
  is_date: boolean;
  not: boolean;
  is_active: boolean;
  from_object?: string;
}

export interface Ruleset {
  id: number;
  name: string;
  operator: LogicOp;
  not: boolean;
  for_object?: string;
  is_active: boolean;
  rules: Rule[];
  children: Ruleset[];
}

// Backend DTO — one entry per (feature, role, root ruleset)
export type FeatureRootRuleset = {
  feature: FeatureType;
  role: RuleType;
  ruleset: Ruleset; // tree
};

// Trace types (for the debug UI)

export type RuleTrace = {
  category: 'RULE';
  id: number;
  name: string;
  field: string;
  operator: ClauseOp;
  left: unknown;
  right: unknown;
  isDate: boolean;
  not: boolean;
  passed: boolean;
  from_object?: string;
};

export type RulesetTrace = {
  category: 'RULESET';
  id: number;
  name: string;
  type: RuleType;
  operator: LogicOp;
  not: boolean;
  isActive: boolean;
  passed: boolean;
  children: Array<RulesetTrace | RuleTrace>;
  for_object?: string;
};

export type TraceForest = RulesetTrace[];

/** === Compiled graph (no recursion; no while) === */
type RuleEvaluator = (form: Record<string, unknown>) => RuleTrace;

/**
 * One compiled node = one ruleset node with:
 * - its rules compiled into checker functions
 * - the indexes of its child nodes (no nested objects)
 */
type RulesetNode = {
  id: number;
  name: string;
  operator: LogicOp;
  not: boolean;
  for_object?: string;
  is_active: boolean;
  ruleEvaluators: RuleEvaluator[];
  childIndexes: number[]; // indices into Graph.nodes
};

// Flat array of nodes + the index of the root
type Graph = {
  nodes: RulesetNode[];
  rootIndex: number;
};

// A single root ruleset, compiled and labeled with feature+role
type CompiledRoot = {
  feature: FeatureType;
  role: RuleType;
  graph: Graph;
};

export class RulesetEngine {
  // All compiled roots for all features
  private static compiledRoots: CompiledRoot[] = [];
  private static initialized = false;

  private static subscribers: Subscriber<any>[] = [];

  // Store the most recent trace forest (for the debugger)
  private static lastTrace: TraceForest | null = null;

  // We fetch once – call this at app start or before first evaluation
  // Fetches all (feature, role, root ruleset) trees from the backend and compile them.
  static async init(): Promise<void> {
    if (this.initialized) return;

    try {
      const rootRows: FeatureRootRuleset[] = await applicationAPI.getRulesets(); // returns [{feature, role, ruleset}, ...]

      this.compiledRoots = rootRows.map((root) => ({
        feature: root.feature,
        role: root.role,
        graph: this.compileGraph(root.ruleset),
      }));
    } catch (error) {
      console.warn('Failed to initialize RulesetEngine:', error);
      this.compiledRoots = [];
    }

    this.initialized = true;
  }

  static addSubscriber(subscriber: Subscriber<any>): void {
    RulesetEngine.subscribers.push(subscriber);
  }

  static removeSubscriber(subscriber: Subscriber<any>): void {
    RulesetEngine.subscribers = RulesetEngine.subscribers.filter(
      (s) => s !== subscriber
    );
  }

  // Notify all subscribers of the event (updates the field value from the Subscriber based on the event/feature type and the result of the evaluation)
  static notify(eventType: string, value: boolean): void {
    if (RulesetEngine.subscribers.length === 0) {
      console.warn('There are no subscribers to RulesetEngine.notify.');
      return;
    }

    RulesetEngine.subscribers.forEach((subscriber) =>
      subscriber.notify(eventType, value)
    );
  }

  // Evaluate only the rulesets belonging to the chosen feature
  // Returns the final boolean (e.g., “PG required?”).
  static evaluate(
    feature: FeatureType,
    data: Partial<ApplicationFormValues>
  ): boolean {
    // 1. Guard: engine must be initialized before evaluation
    if (!this.initialized) {
      console.warn('RulesetEngine.evaluate before init(); returning false.');
      this.lastTrace = [];
      return false;
    }

    // 2. Normalize the input into a plain object we can read
    const form = data as Record<string, unknown>;

    // 3. Pick only the root rulesets for the chosen feature
    const rootsForFeature = this.compiledRoots.filter(
      (root) => root.feature === feature
    );

    // 4. Prepare arrays for the traces: GENERAL (must pass) and OFFRAMP (must fail)
    const general: RulesetTrace[] = [];
    const offramp: RulesetTrace[] = [];

    // 5. Evaluate each root ruleset and store its trace in the appropriate array by role (GENERAL or OFFRAMP)
    for (let i = 0; i < rootsForFeature.length; i++) {
      const { role, graph } = rootsForFeature[i];
      const trace = this.evaluateGraph(graph, role, form);
      (role === 'GENERAL' ? general : offramp).push(trace);
    }

    // 6. Store the traces for the debugger
    this.lastTrace = [...general, ...offramp];

    // 7. Final decision:
    // - If any GENERAL trace passed, return true
    // - If any OFFRAMP trace passed, return false
    const anyGeneral = general.some((t) => t.passed);
    const anyOfframp = offramp.some((t) => t.passed);

    const result = anyGeneral && !anyOfframp;

    return result;
  }

  // Expose the last trace for any UI debugging tool
  static getLastTrace(): TraceForest | null {
    return this.lastTrace;
  }

  // Available features discovered at init (handy for UI tabs/selects)
  static getAvailableFeatures(): FeatureType[] {
    const set = new Set<FeatureType>();
    for (let i = 0; i < this.compiledRoots.length; i++)
      set.add(this.compiledRoots[i].feature);
    return Array.from(set);
  }

  /** ===== Compile to a flat graph (no recursion used) ===== */
  private static compileGraph(root: Ruleset): Graph {
    // Worklist BFS without while: standard for-loop over growing array + seen set
    // 1. Initialize the worklist structures for Breadth-First Search-like traversal
    // - pendingRulesets: queue of rulesets to process
    // - seen: map each ruleset to its node index
    // - nodes: the flat list of compiled nodes
    const pendingRulesets: Ruleset[] = [root];
    const seen = new Map<Ruleset, number>();
    const nodes: RulesetNode[] = [];

    // 2. Traverse the tree: expand the pendingRulesets as we discover new children
    for (let i = 0; i < pendingRulesets.length; i++) {
      const cur = pendingRulesets[i];

      // 3. If unseen, register as a new node and compile its rules
      if (!seen.has(cur)) {
        const idx = nodes.length;
        seen.set(cur, idx);
        nodes.push({
          id: cur.id,
          name: cur.name,
          operator: cur.operator,
          not: cur.not,
          for_object: cur.for_object,
          is_active: cur.is_active,
          ruleEvaluators: cur.rules.map((r) => this.compileRule(r)),
          childIndexes: [],
        });

        // 4. Enqueue any unseen children to visit later
        for (let j = 0; j < cur.children.length; j++) {
          const ch = cur.children[j];
          if (!seen.has(ch)) pendingRulesets.push(ch);
        }
      }
    }

    // 5. Second pass: Wire up children indixes to connect the graph
    for (let i = 0; i < pendingRulesets.length; i++) {
      const cur = pendingRulesets[i];
      const parentIdx = seen.get(cur)!;
      const parentNode = nodes[parentIdx];
      const childIndexes: number[] = [];
      for (let j = 0; j < cur.children.length; j++) {
        childIndexes.push(seen.get(cur.children[j])!);
      }
      parentNode.childIndexes = childIndexes;
    }

    // 6. Return the final flat graph and the root node index
    return { nodes, rootIndex: seen.get(root)! };
  }

  private static compileRule(rule: Rule): RuleEvaluator {
    return (form: Record<string, unknown>): RuleTrace => {
      // 1. Grab the raw left-hand value from the form
      // fallback to resolving a virtual field if non-existent (like current_mmdd)
      const rawFormValue =
        form[rule.rosefield_app_field_name as keyof ApplicationFormValues] ??
        this.resolveVirtualField(rule.rosefield_app_field_name);

      // 2. If the field was flagged as a date, convert it to years ago
      const formValue = rule.is_date
        ? this.yearsAgo(rawFormValue)
        : rawFormValue;

      // 3. Grab the right-hand value from the rule definition
      const ruleValue = rule.value;

      // 4. Compare the left-hand (form) value to the right-hand (rule) value
      let base: boolean;
      if (rule.value === 'NULL') {
        // 4.a. Check if field is empty/null/undefined
        base =
          rawFormValue === null ||
          rawFormValue === undefined ||
          rawFormValue === '';

        // 4.b. Handle the case where the field should have no value (because the rule says "NOT_VALUE")
      } else if (rule.value === 'NOT_VALUE') {
        base = !rawFormValue;
      } else {
        // 5. Normal comparison: check if field has value and compare it to rule's expected value
        const present = rawFormValue !== null && rawFormValue !== undefined;
        base = present && this.compare(rule.operator, formValue, rule.value);
      }

      // 6. Apply the NOT operator if needed
      const passed = rule.not ? !base : !!base;

      // 7. Return the trace object for debugging/inspection
      return {
        category: 'RULE',
        id: rule.id,
        name: rule.name,
        field: rule.rosefield_app_field_name,
        operator: rule.operator,
        isDate: rule.is_date,
        not: rule.not,
        from_object: rule.from_object,
        left: formValue,
        right: ruleValue,
        passed,
      };
    };
  }

  /** ===== Iterative evaluation in ≤ N passes (no while). Detects cycles. ===== */
  private static evaluateGraph(
    graph: Graph,
    role: RuleType,
    formData: Record<string, unknown>
  ): RulesetTrace {
    const { nodes, rootIndex } = graph;

    // Number of nodes to evaluate
    const totalNodes = nodes.length;

    // Arrays to track which nodes have been evaluated and their traces
    const done: boolean[] = new Array(totalNodes).fill(false);
    const traces: (RulesetTrace | null)[] = new Array(totalNodes).fill(null);

    // Up to totalNodes passes: each pass may resolve nodes whose children are done.
    for (let pass = 0; pass < totalNodes; pass++) {
      // flag to track if any nodes were resolved in this pass
      let progressed = false;

      // Iterate over all nodes
      for (let i = 0; i < totalNodes; i++) {
        if (done[i]) continue;

        const node = nodes[i];

        // Only evaluate a node when all of its children have been evaluated
        let kidsReady = true;
        for (let k = 0; k < node.childIndexes.length; k++) {
          if (!done[node.childIndexes[k]]) {
            kidsReady = false;
            break;
          }
        }
        if (!kidsReady) continue;

        // Evaluate this node's rules into ruleTraces
        const ruleTraces: RuleTrace[] = [];
        for (let r = 0; r < node.ruleEvaluators.length; r++) {
          ruleTraces.push(node.ruleEvaluators[r](formData));
        }

        // Get the already-computed traces of this node's children
        const childTraces: RulesetTrace[] = [];
        for (let k = 0; k < node.childIndexes.length; k++) {
          childTraces.push(traces[node.childIndexes[k]]!);
        }

        // Combine children + rules and apply the node's operator (AND/OR)
        const checks = [...childTraces, ...ruleTraces];

        // Apply the node's operator (AND/OR) (if no operator, return false)
        const core =
          checks.length === 0
            ? false
            : node.operator === 'AND'
            ? checks.every((trace) => trace.passed)
            : checks.some((trace) => trace.passed);

        // Apply the node's active flag (if inactive, return false)
        const active = node.is_active ? core : false;

        // Apply the node's NOT operator (if true, return the opposite of active)
        const passed = node.not ? !active : active;

        // Create the trace for this node
        const rulesetTrace: RulesetTrace = {
          category: 'RULESET',
          id: node.id,
          name: node.name,
          type: role,
          operator: node.operator,
          not: node.not,
          isActive: node.is_active,
          passed,
          children: checks,
        };

        // Mark as done and progressed
        traces[i] = rulesetTrace;
        done[i] = true;
        progressed = true;
      }

      // Stop early if everything is evaluated
      let allDone = true;
      for (let i = 0; i < totalNodes; i++)
        if (!done[i]) {
          allDone = false;
          break;
        }
      if (allDone) break;

      // If we made no progress in this pass, the graph has a cycle
      if (!progressed && pass === totalNodes - 1) {
        throw new Error('Ruleset graph has a cycle or unresolved dependency.');
      }
    }

    // Return the root's trace (must exist by now)
    const rootTrace = traces[rootIndex];
    if (!rootTrace) throw new Error('Internal error: root trace missing.');
    return rootTrace;
  }

  // ====== Helper functions ======

  // Returns whole years between today and the given date value
  private static yearsAgo(raw: unknown): number {
    // 1) If the value is missing, return NaN
    if (raw === null || raw === undefined) return NaN;

    const now = new Date();

    // 2) If the value is a Date, return the difference in years
    if (raw instanceof Date) {
      return now.getFullYear() - raw.getFullYear();
    }

    // 3) If the value is a four-digit number, treat it as a calendar year (e.g., 1990)
    if (typeof raw === 'number' && raw > 1000 && raw < 9999) {
      return now.getFullYear() - raw;
    }

    // Fallback: parse as Date string
    const d = new Date(String(raw));

    // If invalid date return NaN, otherwise subtract years
    return Number.isNaN(d.valueOf())
      ? NaN
      : now.getFullYear() - d.getFullYear();
  }

  // Core comparison (no date logic here)
  private static compare(op: ClauseOp, left: unknown, right: unknown): boolean {
    switch (op) {
      case 'EQ':
        return left == right;
      case 'NEQ':
        return left != right;
      case 'GT':
        return Number(left) > Number(right);
      case 'GTE':
        return Number(left) >= Number(right);
      case 'LT':
        return Number(left) < Number(right);
      case 'LTE':
        return Number(left) <= Number(right);
      case 'IN': {
        const list = Array.isArray(right) ? right : String(right).split(',');
        const set = list.map((s) => String(s).trim());
        return set.includes(String(left));
      }
      case 'NIN': {
        const list = Array.isArray(right) ? right : String(right).split(',');
        const set = list.map((s) => String(s).trim());
        return !set.includes(String(left));
      }
      default:
        return false;
    }
  }

  private static resolveVirtualField(field: string) {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();

    switch (field) {
      case 'current_mmdd':
        return month * 100 + day; // e.g.: 0920
      default:
        return undefined;
    }
  }
}
